import { z } from 'zod';
import { isoDate, uuid } from './common';

/**
 * Bringing a history in from another product (PRD 37).
 *
 * The credentials are a body, never a query string: a key in a URL ends up in
 * an access log, a browser's history and a referrer header. They are also never
 * part of any response on this page — nothing here ever gives a key back.
 */
export const fellowCredentials = z.object({
  /** `traceair`, `traceair.fellow.app`, or the address bar — the server reads all three. */
  workspace: z.string().trim().min(1).max(200),
  apiKey: z.string().trim().min(8).max(500),
});

export const fellowPreviewBody = fellowCredentials.extend({
  /** From the previous page's `cursor`. */
  cursor: z.string().max(1000).nullish(),
  /** Only meetings created on or after this day. */
  since: isoDate.nullish(),
  /** Part of a title, case-insensitive — Fellow does the matching. */
  title: z.string().trim().max(255).nullish(),
});
export type FellowPreviewBody = z.infer<typeof fellowPreviewBody>;

/** One of their Fellow meetings, as the picker shows it. */
export const fellowNoteSummary = z.object({
  id: z.string(),
  title: z.string(),
  startedAt: isoDate.nullable(),
  attendees: z.array(z.string()),
  hasRecording: z.boolean(),
  /** Already here from an earlier run — ticking it again would change nothing. */
  alreadyImported: z.boolean(),
});
export type FellowNoteSummary = z.infer<typeof fellowNoteSummary>;

export const fellowPreview = z.object({
  /** Whose history this is, in their words, so a wrong key is obvious at a glance. */
  account: z.string(),
  workspaceName: z.string(),
  notes: z.array(fellowNoteSummary),
  /** Null when that was the last page. */
  cursor: z.string().nullable(),
});
export type FellowPreview = z.infer<typeof fellowPreview>;

/** At most this many in one run: a person choosing, not a switch marked "everything". */
export const IMPORT_LIMIT = 200;

export const startImportBody = fellowCredentials.extend({
  noteIds: z.array(z.string().min(1).max(200)).min(1).max(IMPORT_LIMIT),
  /** Where they land. Null is your own private space. */
  spaceId: uuid.nullish(),
  /** Read them afterwards for commitments and knowledge. A paid model call each. */
  read: z.boolean().default(true),
});
export type StartImportBody = z.infer<typeof startImportBody>;

export const importFailure = z.object({
  noteId: z.string(),
  title: z.string(),
  reason: z.string(),
});
export type ImportFailure = z.infer<typeof importFailure>;

export const importState = z.enum(['QUEUED', 'RUNNING', 'DONE', 'FAILED', 'CANCELLED']);
export type ImportState = z.infer<typeof importState>;

export const importRun = z.object({
  id: uuid,
  source: z.literal('FELLOW'),
  workspace: z.string(),
  spaceId: uuid,
  spaceName: z.string(),
  read: z.boolean(),
  state: importState,
  total: z.number().int(),
  imported: z.number().int(),
  skipped: z.number().int(),
  failures: z.array(importFailure),
  /** Set only when the run itself stopped — a key that was withdrawn, say. */
  error: z.string().nullable(),
  startedAt: isoDate.nullable(),
  finishedAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type ImportRun = z.infer<typeof importRun>;
